import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { fetchBerThongPlayConfig, type BerThongSheet } from '../../services/berThongService'
import { useBerThongUserEntry } from '../../hooks/useBerThongUserEntry'
import type { PointsOutletContext } from './ModernPointsLayout'
import { BerThongCountdown } from './BerThongCountdown'
import { useBerThongNowMs } from './useBerThongNowMs'
import {
  groupSheetsByHuay,
  isClosingSoon,
} from './berThongUi'
import './berThongGame.css'

const BerThongLobbyPage: React.FC = () => {
  const navigate = useNavigate()
  const { themeConfig } = useOutletContext<PointsOutletContext>()
  const { showBerThongEntry, checked } = useBerThongUserEntry(themeConfig.settings.berThongEnabled)
  const [loading, setLoading] = useState(true)
  const [sheets, setSheets] = useState<BerThongSheet[]>([])
  const nowMs = useBerThongNowMs()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetchBerThongPlayConfig()
      setSheets(res.data?.sheets || [])
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'โหลดไม่สำเร็จ',
        text: error instanceof Error ? error.message : 'ไม่สามารถโหลดเบอร์ทอง',
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!checked) return
    if (!showBerThongEntry) {
      setLoading(false)
      return
    }
    void load()
  }, [checked, showBerThongEntry, load])

  const groups = useMemo(() => groupSheetsByHuay(sheets, nowMs), [sheets, nowMs])
  const openGroups = useMemo(() => groups.filter((g) => g.openSheets.length > 0), [groups])

  if (!checked || loading) {
    return <div className="htd-bt__loading">กำลังโหลดเบอร์ทอง...</div>
  }

  if (!showBerThongEntry) {
    return (
      <div className="htd-bt">
        <div className="htd-bt__empty">
          <h2>เบอร์ทองยังไม่เปิด</h2>
          <p>รอแอดมินเปิดสิทธิ์การเล่น</p>
        </div>
      </div>
    )
  }

  return (
    <div className="htd-bt">
      <div className="htd-bt__hero htd-bt__hero--anim">
        <div className="htd-bt__hero-copy">
          <p className="htd-bt__eyebrow">Points Game</p>
          <h2 className="htd-bt__title">เบอร์ทอง</h2>
          <p className="htd-bt__sub">เลือกหวย → เลือกแผ่น → กดเบอร์เงินสด</p>
        </div>
        <Link to="/history/games/ber-thong" className="htd-bt__link">
          ประวัติ
        </Link>
      </div>

      {openGroups.length === 0 ? (
        <div className="htd-bt__empty">
          <h2>ยังไม่มีแผ่นเปิดขาย</h2>
          <p>รอแอดมินเปิดแผ่นเบอร์ทองงวดถัดไป</p>
          <button type="button" className="htd-bt__link" onClick={() => void load()}>
            รีเฟรช
          </button>
        </div>
      ) : (
        <div className="htd-bt__lobby-list">
          {openGroups.map((group, index) => {
            const soon = isClosingSoon(group.soonestCloseAt, nowMs)
            const style = { ['--bt-i' as string]: Math.min(index, 8) } as React.CSSProperties
            return (
              <button
                key={group.key}
                type="button"
                style={style}
                className={`htd-bt__lobby-card htd-bt__lobby-card--rich${soon ? ' is-urgent' : ''}`}
                onClick={() => navigate(`/points/games/ber-thong/huay/${encodeURIComponent(group.key)}`)}
              >
                <div className="htd-bt__lobby-main">
                  <strong>{group.huayName}</strong>
                  <div className="htd-bt__lobby-facts">
                    <span className="htd-bt__fact htd-bt__fact--sheets">
                      {group.openSheets.length} แผ่นเปิดขาย
                    </span>
                  </div>
                  <BerThongCountdown closeAt={group.soonestCloseAt} />
                </div>
                <div className="htd-bt__lobby-side">
                  <span className="htd-bt__lobby-go">เลือก</span>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default BerThongLobbyPage
